import React from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare, Users, GraduationCap, Briefcase, Handshake,
  ShoppingBag, FolderKanban, FileText, ArrowRight, Bell
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

export default function AdminDashboard() {
  const { t } = useLanguage();
  const { stats } = useOutletContext();

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const statCards = [
    { 
      title: 'Messages', 
      value: stats.contacts || 0, 
      pending: stats.unread_contacts || 0,
      icon: MessageSquare,
      color: 'bg-blue-500',
      link: '/admin/contacts'
    },
    { 
      title: 'Candidatures', 
      value: stats.job_applications || 0, 
      pending: stats.pending_applications || 0,
      icon: Briefcase,
      color: 'bg-purple-500',
      link: '/admin/job-applications'
    },
    { 
      title: 'Inscriptions formations', 
      value: stats.training_registrations || 0, 
      pending: stats.pending_registrations || 0,
      icon: GraduationCap,
      color: 'bg-green-500',
      link: '/admin/training-registrations'
    },
    { 
      title: 'Demandes partenariat', 
      value: stats.partnership_requests || 0, 
      pending: stats.pending_partnerships || 0,
      icon: Handshake,
      color: 'bg-orange-500',
      link: '/admin/partnership-requests'
    },
    { 
      title: 'Demandes devis', 
      value: stats.quote_requests || 0, 
      pending: stats.pending_quotes || 0,
      icon: ShoppingBag,
      color: 'bg-pink-500',
      link: '/admin/quote-requests'
    },
  ];

  const contentStats = [
    { title: 'Projets actifs', value: stats.projects || 0, icon: FolderKanban, link: '/admin/projects' },
    { title: 'Produits', value: stats.products || 0, icon: ShoppingBag, link: '/admin/products' },
    { title: 'Formations', value: stats.trainings || 0, icon: GraduationCap, link: '/admin/trainings' },
    { title: 'Offres emploi', value: stats.jobs || 0, icon: Briefcase, link: '/admin/jobs' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-[#0F172A] font-['Montserrat']">
          {t('admin_dashboard')}
        </h1>
        <p className="text-gray-600 mt-1">
          Bienvenue dans votre espace d'administration
        </p>
      </div>

      {/* Alert for pending items */}
      {(stats.unread_notifications > 0) && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mb-6"
        >
          <Card className="border-l-4 border-l-[#E50E24] bg-red-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-[#E50E24]" />
                <span className="font-medium text-[#0F172A]">
                  Vous avez {stats.unread_notifications} notification(s) non lue(s)
                </span>
              </div>
              <Link to="/admin/contacts">
                <Button variant="ghost" size="sm" className="text-[#E50E24]">
                  Voir
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Request Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={stat.link}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`stat-card-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                        <IconComponent className="h-5 w-5 text-white" />
                      </div>
                      {stat.pending > 0 && (
                        <span className="bg-[#E50E24] text-white text-xs px-2 py-1 rounded-full">
                          {stat.pending} en attente
                        </span>
                      )}
                    </div>
                    <div className="text-2xl font-bold text-[#0F172A]">{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.title}</div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Content Stats */}
      <h2 className="text-xl font-bold text-[#0F172A] mb-4">Contenu du site</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {contentStats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ delay: 0.5 + index * 0.1 }}
            >
              <Link to={stat.link}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#0B6AB3]/10 rounded-lg flex items-center justify-center">
                      <IconComponent className="h-6 w-6 text-[#0B6AB3]" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#0F172A]">{stat.value}</div>
                      <div className="text-sm text-gray-500">{stat.title}</div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <h2 className="text-xl font-bold text-[#0F172A] mb-4">Actions rapides</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/services">
          <Button variant="outline" className="w-full justify-start gap-2 h-12">
            <FileText className="h-5 w-5 text-[#0B6AB3]" />
            Gérer les services
          </Button>
        </Link>
        <Link to="/admin/projects">
          <Button variant="outline" className="w-full justify-start gap-2 h-12">
            <FolderKanban className="h-5 w-5 text-[#0B6AB3]" />
            Ajouter un projet
          </Button>
        </Link>
        <Link to="/admin/products">
          <Button variant="outline" className="w-full justify-start gap-2 h-12">
            <ShoppingBag className="h-5 w-5 text-[#0B6AB3]" />
            Ajouter un produit
          </Button>
        </Link>
        <Link to="/admin/jobs">
          <Button variant="outline" className="w-full justify-start gap-2 h-12">
            <Briefcase className="h-5 w-5 text-[#0B6AB3]" />
            Publier une offre
          </Button>
        </Link>
      </div>
    </div>
  );
}
